import { moment } from '@feathr/hooks';
import type { TConstraints } from '@feathr/rachis';

import { TargetableClass } from './targetable.types';
import type { ITargetableBase } from './targetable_base';
import { TargetableBase } from './targetable_base';

export interface IGeoFencingPlace {
  id: string;
  lat: number;
  lng: number;
  name?: string;
}

/**
 * Base interface for geofencing targetables
 */
interface IFactualTargetableBase extends ITargetableBase {
  _cls: TargetableClass.factual;
  places: IGeoFencingPlace[];
  units: 'kilometers' | 'meters';
  radius: number;
  file_name?: string;
  file_url?: string;
}

/**
 * Interface for historical geofencing targetables
 */
export interface IHistoricalFactualTargetable extends IFactualTargetableBase {
  is_historical: true;
  date_start: string;
  date_end: string;
}

/**
 * Interface for mobile geofencing targetables
 */
export interface IMobileGeofencingTargetable extends IFactualTargetableBase {
  is_historical?: false;
}

/**
 * Union type for factual (geofencing) targetables
 */
export type IFactualTargetable = IHistoricalFactualTargetable | IMobileGeofencingTargetable;

/**
 * FactualTargetable class for factual (geofencing) targetables
 */
export class FactualTargetable extends TargetableBase<IFactualTargetable> {
  public override className = 'Targetable.FactualTargetable';

  public override getDefaults(): Partial<IFactualTargetable> {
    return {
      ...super.getDefaults(),
      _cls: TargetableClass.factual,
    };
  }

  public override get constraints(): TConstraints<IFactualTargetable> {
    return {
      ...super.constraints,
      _cls: {
        inclusion: [TargetableClass.factual],
      },
      places: {
        length: {
          minimum: 1,
          message: '^Please add a place to your target.',
        },
      },
      radius: (...args: unknown[]) => {
        const attributes = args[1] as IFactualTargetable;
        const { radius, units } = attributes;
        if (radius < 1000) {
          return {
            numericality: {
              greaterThan: 1000,
              message:
                units === 'meters'
                  ? '^Radius must be greater than 1,000 meters.'
                  : '^Radius must be greater than 1 kilometers.',
            },
          };
        }
        if (radius > 30000) {
          return {
            numericality: {
              lessThan: 30000,
              message:
                units === 'meters'
                  ? '^Radius must be less than 30,000 meters.'
                  : '^Radius must be less than 30 kilometers.',
            },
          };
        }
        return undefined;
      },
      date_start: (...args: unknown[]) => {
        const attributes = args[1] as IFactualTargetable;
        const { date_end, is_historical } = attributes;
        if (is_historical) {
          return {
            presence: {
              allowEmpty: false,
              message: '^Please choose a start date for your target',
            },
            datetime: {
              latest: moment.utc(date_end).subtract(2, 'days'),
              message: '^Start date must be at least two days earlier than the end date.',
              dateOnly: true,
            },
          };
        }
        return undefined;
      },
      date_end: (...args: unknown[]) => {
        const attributes = args[1] as IFactualTargetable;
        const { is_historical } = attributes;
        if (is_historical) {
          return {
            presence: {
              allowEmpty: false,
              message: '^Please choose an end date for your target',
            },
            datetime: {
              latest: moment().subtract(30, 'days'),
              message: '^End date must be at least 30 days in the past.',
              dateOnly: true,
            },
          };
        }
        return undefined;
      },
      is_historical: {
        presence: {
          allowEmpty: true,
          message: '^Please choose an end date for your target',
        },
      },
    };
  }
}
